import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createStyles, makeStyles } from '@mui/styles';
import { OrganizationPhaseCommitments } from 'api/commitments';

const useStyles = makeStyles(() =>
    createStyles({
        deploymentContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '12px 0px',
            width: '100%',
            alignItems: 'top',
        },
        deploymentColor: {
            borderRadius: '4px',
            height: '14px',
            width: '14px',
            flexShrink: 0
        },
        deploymentName: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            fontSize: '14px',
            fontWeight: 700,
            color: '#1F2051',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maxWidth: '95%'
        },
        deploymentStats: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            fontSize: '14px',
            fontWeight: 300,
            color: '#5A6A81'
        },
        progressBarContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
        }
    })
);

type ComputedRange = {
    filledRange: number[];
    unfilledRange: number[];
    color: string;
    name: string;
};

const minBarWidth = 2;
const maxBarWidth = 4;
const minSpacing = 1.5;
const maxSpacing = 4;

type SegmentedProgressBarProps = {
    commitments?: OrganizationPhaseCommitments;
};

const SegmentedProgressBar: React.FC<SegmentedProgressBarProps> = ({ commitments }) => {
    const classes = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [barData, setBarData] = useState<{
        totalBars: number;
        barWidth: number;
        spacing: number;
        ranges: ComputedRange[];
    } | null>(null);

    const divisions = useMemo(() => {
        const colors = [
            '#7EEBCC', // mint green (cool) 
            '#F58BB0', // pastel pink (warm) 
            '#90C5F2', // light blue (cool)
            '#F4A78C', // soft coral (warm)
            '#C6A2F2', // lavender purple (cool)
            '#FFAED6', // light rose (warm) 
            '#A3E7E4'  // fresh aqua (cool) 
        ];

        return commitments?.commitments
            ?.sort((a, b) => {
                if (b.seats_used_actual_in_phase !== a.seats_used_actual_in_phase) {
                    return b.seats_used_actual_in_phase - a.seats_used_actual_in_phase;
                }
                return b.seat_total - a.seat_total;
            })
            ?.map((commitment, index) => ({
                filled: commitment.seats_used_actual_in_phase,
                total: commitment.seat_total,
                color: colors[index % colors.length],
                name: commitment.deployment_name
            })) || [];
    }, [commitments]);

    const calculateBarData = useCallback(() => {
        if (!containerRef.current || divisions.length === 0) return;
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        
        const goal = divisions.reduce((sum, d) => sum + d.total, 0);
        if (goal === 0) return;

        let bestFit: { barWidth: number; spacing: number; totalBars: number } | null = null;
        let closestToGoal = Number.MAX_VALUE;

        for (let barWidth = maxBarWidth; barWidth >= minBarWidth; barWidth -= 0.1) {
            for (let spacing = minSpacing; spacing <= maxSpacing; spacing += 0.1) {
                const unit = barWidth + spacing;
                const bars = Math.floor((width + spacing) / unit);
                const diff = Math.abs(bars - goal);
                
                // Only consider if it fits perfectly within width
                if (diff < closestToGoal) {
                    closestToGoal = diff;
                    bestFit = { barWidth, spacing, totalBars: bars };
                }
            }
        }

        if (!bestFit) return;

        let currentStart = 0;
        const ranges: ComputedRange[] = divisions.map((div) => {
            const portion = div.total / goal;
            const barCount = Math.round(portion * bestFit!.totalBars);
            const filledBars = Math.round((div.filled / div.total) * barCount);
            const filledRange = Array.from({ length: filledBars }, (_, i) => currentStart + i);
            const unfilledRange = Array.from({ length: barCount - filledBars }, (_, i) => currentStart + filledBars + i);
            currentStart += barCount;

            return { filledRange, unfilledRange, color: div.color, name: div.name };
        });

        setBarData({ ...bestFit, ranges });
    }, [divisions]);

    useEffect(() => {
        calculateBarData();

        let timeoutId: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                calculateBarData();
            }, 100); // 100ms debounce
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [calculateBarData]);

    // Force recalculation when container width changes
    useEffect(() => {
        calculateBarData();
    }, [containerWidth, calculateBarData]);

    const renderDeploymentStats = () => (
        <div className={classes.deploymentContainer}>
            {divisions.map((division, index) => (
                <div key={index} className={classes.deploymentStats}>
                    <div className={classes.deploymentName}>
                        <div
                            className={classes.deploymentColor}
                            style={{ background: division.color }}
                        />
                        <span>
                            <b>{division.name}</b>
                        </span>
                    </div>
                    <span>
                        {division.filled} / {division.total} <span style={{ fontSize: '12px' }}>{division.filled >= division.total && '(COMPLETED 🎉)'}</span>
                    </span>
                </div>
            ))}
        </div>
    );

    return (
        <div className={classes.progressBarContainer}>
            {!barData ? (
                <div ref={containerRef} style={{ height: 32 }} />
            ) : (
                <div 
                    key={`bars-${containerWidth}`}
                    ref={containerRef} 
                    style={{ display: 'flex', flexWrap: 'nowrap', overflow: 'hidden', height: 32, justifyContent: 'space-evenly' }}
                >
                    {barData.ranges.flatMap(({ filledRange, unfilledRange, color }) => [
                        ...filledRange.map((_, i) => (
                            <div
                                key={`f-${color}-${i}`}
                                style={{
                                    width: barData.barWidth,
                                    height: '100%',
                                    backgroundColor: color,
                                    marginRight: 0,
                                    borderRadius: barData.barWidth / 2,
                                }}
                            />
                        )),
                        ...unfilledRange.map((_, i) => (
                            <div
                                key={`u-${color}-${i}`}
                                style={{
                                    width: barData.barWidth,
                                    height: '100%',
                                    backgroundColor: `${color}26`,
                                    marginRight: 0,
                                    borderRadius: barData.barWidth / 2,
                                }}
                            />
                        )),
                    ])}
                </div>
            )}
            {divisions.length > 1 && renderDeploymentStats()}
        </div>
    );
};

export default SegmentedProgressBar; 